import { FC, ReactNode, useEffect, useState } from 'react';
import { Collapse } from '@mui/material';
import { faArrowRight, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import QcsAlert from '../common/basic/QcsAlert';
import { QcsClickToCopyText } from '../common/basic/QcsClickToCopyText';
import { QcsLoadingButton } from '../common/basic/QcsLoadingButton';
import { FaIcon } from '../common/FaIcon';
import { resources } from '@api/resources';
import { Actions, Card, EnteredButton, EnteredMark, HeaderRow, Main, Meta, Name, PinRow } from './VisitorCard.styles';

export type VisitorCardStatus = 'pending' | 'training-required' | 'submitting' | 'entered' | 'error';

export interface VisitorCardPerson {
  id: string;
  name: string;
  lastName: string;
  company?: string;
  phone?: string;
}

export interface VisitorCardProps {
  person: VisitorCardPerson;
  pin: string;
  status: VisitorCardStatus;
  timestamp?: string;
  hasRequiredTraining: boolean;
  onEnter: () => void;
  trainingSlot?: ReactNode;
  errorMessage?: ReactNode;
  disabled?: boolean;
}

export const VisitorCard: FC<VisitorCardProps> = ({
  person,
  pin,
  status,
  timestamp,
  errorMessage,
  hasRequiredTraining,
  onEnter,
  trainingSlot,
  disabled,
}) => {
  const { t } = useTranslation();
  const fullName = `${person.name} ${person.lastName}`.trim();
  const isEntered = status === 'entered';
  const isBusy = status === 'submitting';
  const metaParts = [person.company, person.phone].filter(Boolean).join(' · ');
  const enteredText = timestamp ? t('visitation.oneTimeEnteredAt', { time: timestamp }) : t('visitation.oneTimeEntered');

  const [expanded, setExpanded] = useState(false);
  useEffect(() => {
    if (!isEntered) setExpanded(false);
  }, [isEntered]);

  const compact = isEntered && !expanded;
  const showBody = !isEntered || expanded;

  const handleCardClick = () => {
    if (isEntered) setExpanded((prev) => !prev);
  };

  return (
    <Card
      $state={status}
      $compact={compact}
      $clickable={isEntered}
      onClick={handleCardClick}
      role={isEntered ? 'button' : undefined}
      tabIndex={isEntered ? 0 : undefined}
      onKeyDown={
        isEntered
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded((prev) => !prev);
              }
            }
          : undefined
      }
      aria-expanded={isEntered ? expanded : undefined}
      aria-labelledby={`visitor-name-${person.id}`}
    >
      <Main>
        <HeaderRow>
          <Name id={`visitor-name-${person.id}`} title={fullName}>
            {fullName}
          </Name>
          <Collapse in={compact} orientation="horizontal" timeout={280} unmountOnExit>
            <EnteredMark>{enteredText}</EnteredMark>
          </Collapse>
        </HeaderRow>

        <Collapse in={showBody} timeout={280} unmountOnExit>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
            {metaParts && <Meta>{metaParts}</Meta>}

            {pin && (
              <PinRow>
                <span>{t('visitation.pin')}</span>
                <span className="pin-value">
                  <QcsClickToCopyText text={pin} />
                </span>
              </PinRow>
            )}

            {status === 'error' && errorMessage && (
              <QcsAlert severity="error" sx={{ mt: 0.5 }}>
                {errorMessage}
              </QcsAlert>
            )}
          </div>
        </Collapse>
      </Main>

      {showBody && (
        <Actions onClick={(e) => e.stopPropagation()}>
          {hasRequiredTraining && !isEntered && trainingSlot}
          {isEntered ? (
            <EnteredButton>
              <FaIcon icon={faCheck} />
              {enteredText}
            </EnteredButton>
          ) : (
            <QcsLoadingButton
              variant="contained"
              color="primary"
              onClick={onEnter}
              disabled={disabled || !pin}
              loading={isBusy}
              resourceLoading={resources.visit}
            >
              <FaIcon icon={faArrowRight} style={{ marginRight: 8 }} />
              {t('visitation.oneTimeEnter')}
            </QcsLoadingButton>
          )}
        </Actions>
      )}
    </Card>
  );
};
